/* Kashira cart motion — one vocabulary for the drawer (spec 04) and the cart
   page (spec 16):
     · drawer swipe-right-to-close (touch only; ✕ / scrim / ESC untouched)
     · new line item enters fade + 12px rise 300ms
     · quantity change crossfades the price/number 150ms
     · remove collapses the row height 250ms before the section re-renders
   Dawn replaces whole section innerHTML on every cart mutation, so enter and
   crossfade are detected by diffing the rendered rows, not by patching Dawn's
   fetch. Every effect is CSS-class driven and every class has a
   prefers-reduced-motion fallback in kashira-theme.css. */
(() => {
  if (window.__kashiraCartMotion) return;
  window.__kashiraCartMotion = true;

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const COLLAPSE_MS = 250;

  /* ---------------------------------------------------------------------
     1. Remove → 250ms row-height collapse before the row disappears.
     CartRemoveButton calls cartItems.updateQuantity(index, 0, event). We wrap
     that method so the collapse plays first, then hand off to Dawn unchanged.
     The UNDO toast (kashira-cart-undo.js) listens on its own click handler and
     is unaffected.
     --------------------------------------------------------------------- */
  const patchRemoveCollapse = () => {
    // A top-level `class` in a classic script binds globally but is NOT a
    // window property, so resolve it by name with a typeof guard.
    const CartItemsClass = typeof CartItems !== 'undefined' ? CartItems : null;
    if (!CartItemsClass) return false; // cart.js not evaluated yet — keep polling
    if (CartItemsClass.prototype.__kashiraCollapsePatched) return true;

    const original = CartItemsClass.prototype.updateQuantity;
    if (typeof original !== 'function') return true;

    CartItemsClass.prototype.updateQuantity = function (line, quantity, event, name, variantId) {
      const row = this.querySelector(`#CartItem-${line}`) || this.querySelector(`#CartDrawer-Item-${line}`);

      if (quantity !== 0 || !row || REDUCED.matches) {
        return original.call(this, line, quantity, event, name, variantId);
      }

      // `event.currentTarget` is nulled once dispatch finishes, and Dawn reads
      // it to label a performance marker — capture it in a plain shim.
      const shim = { currentTarget: event && event.currentTarget };

      // Lock the list NOW, not after the collapse: /cart/change addresses
      // lines by index, so a second Remove inside the 250ms window would
      // shift the numbering and delete the wrong item.
      this.enableLoading(line);

      const lineKey = row.querySelector('input[data-quantity-line-key]')?.dataset.quantityLineKey;
      row.style.setProperty('--kashira-row-height', `${row.getBoundingClientRect().height}px`);
      row.classList.add('kashira-line-collapse');

      // The collapse must not outlive a failed removal — the fill state pins
      // the row at height 0 while the item is still in the cart and totals.
      // cartUpdate fires even on the errors path (cartData.errors set); a
      // network failure publishes nothing, hence the timeout backstop.
      const revert = () => {
        if (!row.isConnected) return;
        row.classList.remove('kashira-line-collapse');
        row.style.removeProperty('--kashira-row-height');
      };
      let settle = null;
      const timeout = window.setTimeout(() => {
        settle?.();
        revert();
      }, 10000);
      settle = subscribe(PUB_SUB_EVENTS.cartUpdate, (payload) => {
        if (payload?.source !== 'cart-items') return;
        window.clearTimeout(timeout);
        settle?.();
        const items = payload.cartData?.items;
        const failed =
          payload.cartData?.errors || (lineKey && Array.isArray(items) && items.some((i) => i.key === lineKey));
        if (failed) revert();
      });

      window.setTimeout(() => original.call(this, line, quantity, shim, name, variantId), COLLAPSE_MS);
      return undefined;
    };

    CartItemsClass.prototype.__kashiraCollapsePatched = true;
    return true;
  };

  // cart.js is deferred and defines CartItems as a global class binding; poll a
  // few frames rather than racing script order.
  if (!patchRemoveCollapse()) {
    let tries = 0;
    const timer = window.setInterval(() => {
      if (patchRemoveCollapse() || ++tries > 40) window.clearInterval(timer);
    }, 50);
  }

  /* ---------------------------------------------------------------------
     2 + 3. New line enter (fade + 12px rise 300ms) and quantity/price
     crossfade (150ms). Rows are keyed by their AJAX line key so a re-render
     that merely changes a price is told apart from a genuinely new line.
     --------------------------------------------------------------------- */
  const PRICE_SELECTOR =
    '.cart-item__price-wrapper .price, .cart-item__final-price, .cart-item__old-price, .cart-item__totals .price';

  const lineKeyOf = (row) => {
    const input = row.querySelector('input[data-quantity-line-key]');
    return input?.dataset.quantityLineKey || row.id || null;
  };

  const priceTextOf = (row) =>
    Array.from(row.querySelectorAll(PRICE_SELECTOR))
      .map((el) => el.textContent.trim())
      .join('|');

  // key -> last rendered price string. Seeded on first pass so the initial
  // server render never animates.
  const seen = new Map();
  let seeded = false;

  const decorate = () => {
    const rows = document.querySelectorAll('cart-drawer .cart-item, cart-items .cart-item');
    const next = new Map();

    rows.forEach((row) => {
      const key = lineKeyOf(row);
      if (!key) return;
      const price = priceTextOf(row);
      next.set(key, price);

      if (!seeded) return;

      if (!seen.has(key)) {
        // New line: fade + 12px rise, 300ms.
        row.classList.add('kashira-line-enter');
        row.addEventListener('animationend', () => row.classList.remove('kashira-line-enter'), { once: true });
      } else if (seen.get(key) !== price) {
        // Quantity changed: crossfade the price/number, 150ms.
        row.querySelectorAll(PRICE_SELECTOR).forEach((el) => {
          el.classList.remove('kashira-price-swap');
          // Force a reflow so a repeated change restarts the animation.
          void el.offsetWidth;
          el.classList.add('kashira-price-swap');
          el.addEventListener('animationend', () => el.classList.remove('kashira-price-swap'), { once: true });
        });
      }
    });

    seen.clear();
    next.forEach((value, key) => seen.set(key, value));
    seeded = true;
  };

  let frame = null;
  const scheduleDecorate = () => {
    if (frame) return;
    frame = window.requestAnimationFrame(() => {
      frame = null;
      decorate();
    });
  };

  const observeCartRoot = (root) => {
    if (!root || root.__kashiraObserved) return;
    root.__kashiraObserved = true;
    new MutationObserver(scheduleDecorate).observe(root, { childList: true, subtree: true });
  };

  const bootObservers = () => {
    document.querySelectorAll('cart-drawer, cart-items, cart-drawer-items').forEach(observeCartRoot);
    decorate();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootObservers, { once: true });
  } else {
    bootObservers();
  }

  /* ---------------------------------------------------------------------
     4. Drawer swipe-right-to-close (spec 04 mobile). Passive listeners only:
     we never call preventDefault, so vertical scrolling inside the item list
     and every click/keyboard path (✕, scrim, ESC) keep working. The panel only
     follows the finger once the gesture is unambiguously horizontal and
     rightward, and a tap below threshold snaps back.
     --------------------------------------------------------------------- */
  const SWIPE_ARM_PX = 10; // gesture must travel this far before we claim it
  const SWIPE_CLOSE_PX = 70; // distance that closes on release
  const SWIPE_CLOSE_VELOCITY = 0.5; // px/ms — spec 04 "velocity > 0.5 closes"

  const bindSwipe = (drawer) => {
    if (!drawer || drawer.__kashiraSwipeBound) return;
    drawer.__kashiraSwipeBound = true;

    let panel = null;
    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let dx = 0;
    let mode = null; // null = undecided, 'swipe', 'ignore'

    const reset = () => {
      if (panel) {
        panel.classList.remove('kashira-drawer-swiping');
        panel.style.removeProperty('--kashira-swipe-x');
      }
      panel = null;
      mode = null;
      dx = 0;
    };

    drawer.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length !== 1 || !drawer.classList.contains('active')) return;
        panel = drawer.querySelector('.drawer__inner');
        if (!panel) return;
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
        startTime = event.timeStamp;
        dx = 0;
        mode = null;
      },
      { passive: true }
    );

    drawer.addEventListener(
      'touchmove',
      (event) => {
        if (!panel || mode === 'ignore' || event.touches.length !== 1) return;
        dx = event.touches[0].clientX - startX;
        const dy = event.touches[0].clientY - startY;

        if (mode === null) {
          if (Math.abs(dx) < SWIPE_ARM_PX && Math.abs(dy) < SWIPE_ARM_PX) return;
          // Vertical or leftward intent belongs to the scroller, not to us.
          mode = dx > 0 && Math.abs(dx) > Math.abs(dy) ? 'swipe' : 'ignore';
          if (mode === 'ignore') return;
          if (!REDUCED.matches) panel.classList.add('kashira-drawer-swiping');
        }

        // Reduced motion: track the gesture, but never drag the panel around.
        if (!REDUCED.matches) panel.style.setProperty('--kashira-swipe-x', `${Math.max(0, dx)}px`);
      },
      { passive: true }
    );

    const onEnd = (event) => {
      if (!panel) return;
      const elapsed = Math.max(1, event.timeStamp - startTime);
      const velocity = dx / elapsed;
      const shouldClose = mode === 'swipe' && (dx > SWIPE_CLOSE_PX || velocity > SWIPE_CLOSE_VELOCITY);
      reset();
      if (shouldClose) drawer.close();
    };

    drawer.addEventListener('touchend', onEnd, { passive: true });
    drawer.addEventListener('touchcancel', reset, { passive: true });
  };

  /* ---------------------------------------------------------------------
     5. Coupon field vs the mobile software keyboard (spec 04 mobile: "keyboard
     for coupon pushes footer up (visualViewport)"). The drawer is a fixed,
     full-height panel, so the layout viewport never moves when the keyboard
     opens and the footer — coupon field included — stays underneath it.
     visualViewport reports the covered strip; publishing it as
     --kashira-kb-inset and spending it as bottom padding on the border-box
     panel shortens the panel by exactly that much, carrying the footer clear.
     Android browsers that resize the layout viewport instead report a zero
     inset, so the formula leaves those alone.
     --------------------------------------------------------------------- */
  const KEYBOARD_MQ = window.matchMedia('(max-width: 749px)');

  const bindKeyboardInset = (drawer) => {
    const viewport = window.visualViewport;
    if (!drawer || !viewport || drawer.__kashiraKeyboardBound) return;
    drawer.__kashiraKeyboardBound = true;

    let panel = null;

    const clear = () => {
      if (panel) panel.style.removeProperty('padding-bottom');
      document.documentElement.style.removeProperty('--kashira-kb-inset');
      panel = null;
    };

    const apply = () => {
      if (!panel) return;
      if (!KEYBOARD_MQ.matches) {
        clear();
        return;
      }
      const inset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
      document.documentElement.style.setProperty('--kashira-kb-inset', `${Math.round(inset)}px`);
      panel.style.paddingBottom = 'var(--kashira-kb-inset)';
    };

    drawer.addEventListener('focusin', (event) => {
      if (!KEYBOARD_MQ.matches || !event.target.closest('input, textarea')) return;
      panel = drawer.querySelector('.drawer__inner');
      apply();
    });

    drawer.addEventListener('focusout', clear);
    // The keyboard animates in after focus, so the real inset only arrives here.
    viewport.addEventListener('resize', apply);
    viewport.addEventListener('scroll', apply);
  };

  const bootSwipe = () => {
    const drawer = document.querySelector('cart-drawer');
    bindSwipe(drawer);
    bindKeyboardInset(drawer);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootSwipe, { once: true });
  } else {
    bootSwipe();
  }

  /* ---------------------------------------------------------------------
     6. Cart mutations replace whole containers (the drawer's .drawer__inner,
     the rail's .js-contents), snapping accordions shut and discarding a
     typed-but-unapplied coupon code or unsaved gift-note keystrokes. Snapshot
     the shopper's in-progress state as they interact; re-apply it after each
     swap. Server-rendered values stay authoritative — only genuinely newer
     input wins.
     --------------------------------------------------------------------- */
  const WIDGET_SCOPES = [
    ['cart-drawer', '.drawer__inner'],
    ['#main-cart-footer', '.js-contents'],
  ];
  const widgetState = new Map(); // scope selector -> snapshot

  const snapshotWidgets = (root, scopeSel) => {
    const scope = root.querySelector(scopeSel);
    if (!scope) return;
    widgetState.set(scopeSel, {
      openDetails: [...scope.querySelectorAll('details[id][open]')].map((d) => d.id),
      coupon: scope.querySelector('.kashira-coupon input')?.value || '',
      note: scope.querySelector('textarea[name="note"]')?.value ?? null,
    });
  };

  const restoreWidgets = (root, scopeSel) => {
    const saved = widgetState.get(scopeSel);
    const scope = root.querySelector(scopeSel);
    if (!saved || !scope) return;
    saved.openDetails.forEach((id) => {
      const details = scope.querySelector(`details[id="${id}"]`);
      if (details && !details.open) details.open = true;
    });
    const coupon = scope.querySelector('.kashira-coupon input');
    if (coupon && !coupon.value && saved.coupon) coupon.value = saved.coupon;
    const note = scope.querySelector('textarea[name="note"]');
    if (note && saved.note !== null && note.value !== saved.note && document.activeElement !== note) {
      note.value = saved.note;
    }
  };

  const captureWidgets = (event) => {
    WIDGET_SCOPES.forEach(([rootSel, scopeSel]) => {
      const root = document.querySelector(rootSel);
      if (root && root.querySelector(scopeSel)?.contains(event.target)) snapshotWidgets(root, scopeSel);
    });
  };
  // `toggle` does not bubble; capture phase reaches it anyway.
  document.addEventListener('input', captureWidgets, true);
  document.addEventListener('toggle', captureWidgets, true);

  const bootWidgetPreservation = () => {
    WIDGET_SCOPES.forEach(([rootSel, scopeSel]) => {
      const root = document.querySelector(rootSel);
      if (!root || root.__kashiraWidgetsObserved) return;
      root.__kashiraWidgetsObserved = true;
      new MutationObserver(() =>
        window.requestAnimationFrame(() => restoreWidgets(root, scopeSel))
      ).observe(root, { childList: true, subtree: true });
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootWidgetPreservation, { once: true });
  } else {
    bootWidgetPreservation();
  }
})();
